import { useState, useEffect } from 'react';
import { X, AlertCircle, Info, AlertTriangle, AlertOctagon } from 'lucide-react';
import { api } from '../services/api';
import { socket } from '../services/socket';
import { useAuth } from '../contexts/AuthContext';

interface Broadcast {
  id: string;
  title: string;
  message: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  lastSentAt: string | null;
  creator: {
    id: string;
    name: string;
    email: string;
  };
}

export default function BroadcastBanner() {
  const { agent } = useAuth();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [dismissedBroadcasts, setDismissedBroadcasts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!agent?.id) return; // Don't load if no user logged in

    loadActiveBroadcasts();

    // Get localStorage key for current user
    const dismissedKey = `dismissedBroadcasts_${agent.id}`;

    // Load dismissed broadcasts with their dismiss timestamp from localStorage (per user)
    const dismissed = localStorage.getItem(dismissedKey);
    if (dismissed) {
      try {
        const parsed = JSON.parse(dismissed);
        // Migrate old format (array) to new format (object with timestamps)
        if (Array.isArray(parsed)) {
          const migratedData: Record<string, string> = {};
          parsed.forEach((id: string) => {
            migratedData[id] = new Date().toISOString();
          });
          setDismissedBroadcasts(migratedData);
          localStorage.setItem(dismissedKey, JSON.stringify(migratedData));
        } else {
          setDismissedBroadcasts(parsed);
        }
      } catch (e) {
        console.error('Error parsing dismissed broadcasts:', e);
      }
    }

    // Listen for WebSocket events
    socket.on('new-broadcast', (broadcast: Broadcast) => {
      console.log('New broadcast received:', broadcast);
      if (broadcast.isActive) {
        setBroadcasts((prev) => [broadcast, ...prev]);
      }
    });

    socket.on('broadcast-updated', (broadcast: Broadcast) => {
      console.log('Broadcast updated:', broadcast);

      setBroadcasts((prev) => {
        const filtered = prev.filter((b) => b.id !== broadcast.id);
        if (broadcast.isActive) {
          return [broadcast, ...filtered];
        }
        return filtered;
      });
    });

    socket.on('broadcast-resent', (data: { broadcast: Broadcast; sendToAll: boolean }) => {
      console.log('Broadcast resent:', data);

      // Remove from dismissed list so it shows up again
      setDismissedBroadcasts((prev) => {
        const updated = { ...prev };
        delete updated[data.broadcast.id];
        localStorage.setItem(dismissedKey, JSON.stringify(updated));
        return updated;
      });

      setBroadcasts((prev) => {
        const filtered = prev.filter((b) => b.id !== data.broadcast.id);
        if (data.broadcast.isActive) {
          return [data.broadcast, ...filtered];
        }
        return filtered;
      });
    });

    socket.on('broadcast-deleted', (data: { id: string }) => {
      console.log('Broadcast deleted:', data.id);
      setBroadcasts((prev) => prev.filter((b) => b.id !== data.id));
    });

    return () => {
      socket.off('new-broadcast');
      socket.off('broadcast-updated');
      socket.off('broadcast-resent');
      socket.off('broadcast-deleted');
    };
  }, [agent?.id]);

  const loadActiveBroadcasts = async () => {
    try {
      const response = await api.getBroadcasts({ active: true });
      setBroadcasts(response.broadcasts || []);
    } catch (error) {
      console.error('Error loading active broadcasts:', error);
    }
  };

  const handleDismiss = (id: string) => {
    if (!agent?.id) return; // Don't save if no user logged in

    const updated = {
      ...dismissedBroadcasts,
      [id]: new Date().toISOString()
    };
    setDismissedBroadcasts(updated);
    const dismissedKey = `dismissedBroadcasts_${agent.id}`;
    localStorage.setItem(dismissedKey, JSON.stringify(updated));
  };

  const getPriorityConfig = (priority: Broadcast['priority']) => {
    switch (priority) {
      case 'URGENT':
        return {
          bgColor: 'bg-red-100 dark:bg-red-900/30',
          borderColor: 'border-red-300 dark:border-red-700',
          textColor: 'text-red-900 dark:text-red-100',
          icon: AlertOctagon,
          iconColor: 'text-red-600 dark:text-red-400',
        };
      case 'HIGH':
        return {
          bgColor: 'bg-orange-100 dark:bg-orange-900/30',
          borderColor: 'border-orange-300 dark:border-orange-700',
          textColor: 'text-orange-900 dark:text-orange-100',
          icon: AlertTriangle,
          iconColor: 'text-orange-600 dark:text-orange-400',
        };
      case 'NORMAL':
        return {
          bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          borderColor: 'border-blue-300 dark:border-blue-700',
          textColor: 'text-blue-900 dark:text-blue-100',
          icon: Info,
          iconColor: 'text-blue-600 dark:text-blue-400',
        };
      case 'LOW':
        return {
          bgColor: 'bg-gray-100 dark:bg-gray-700',
          borderColor: 'border-gray-300 dark:border-gray-600',
          textColor: 'text-gray-900 dark:text-gray-100',
          icon: AlertCircle,
          iconColor: 'text-gray-600 dark:text-gray-400',
        };
      default:
        return {
          bgColor: 'bg-gray-100 dark:bg-gray-700',
          borderColor: 'border-gray-300 dark:border-gray-600',
          textColor: 'text-gray-900 dark:text-gray-100',
          icon: Info,
          iconColor: 'text-gray-600 dark:text-gray-400',
        };
    }
  };

  // Filter out dismissed broadcasts, but show them again if they were resent after being dismissed
  const visibleBroadcasts = broadcasts.filter((broadcast) => {
    const dismissedAt = dismissedBroadcasts[broadcast.id];

    // If never dismissed, show it
    if (!dismissedAt) return true;

    // If dismissed, check if it was resent after being dismissed
    if (broadcast.lastSentAt) {
      const lastSent = new Date(broadcast.lastSentAt);
      const dismissed = new Date(dismissedAt);
      // Show if it was sent after user dismissed it
      return lastSent > dismissed;
    }

    // If no lastSentAt but was dismissed, don't show
    return false;
  });

  if (visibleBroadcasts.length === 0) return null;

  return (
    <div className="space-y-2">
      {visibleBroadcasts.map((broadcast) => {
        const config = getPriorityConfig(broadcast.priority);
        const Icon = config.icon;

        return (
          <div
            key={broadcast.id}
            className={`${config.bgColor} ${config.borderColor} border rounded-lg p-3 flex items-start gap-3`}
          >
            <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <h4 className={`font-semibold ${config.textColor} text-sm mb-1`}>
                {broadcast.title}
              </h4>
              <p className={`${config.textColor} text-sm whitespace-pre-wrap mb-2`}>
                {broadcast.message}
              </p>
              <div className={`text-xs ${config.textColor} opacity-75 space-y-1 border-t ${config.borderColor} pt-2 mt-2`}>
                {broadcast.lastSentAt && (
                  <p>
                    📅 Enviado em: {new Date(broadcast.lastSentAt).toLocaleString('pt-BR')}
                  </p>
                )}
                {broadcast.expiresAt && (
                  <p>
                    ⏰ Válido até: {new Date(broadcast.expiresAt).toLocaleString('pt-BR')}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleDismiss(broadcast.id)}
              className={`${config.iconColor} hover:opacity-70 transition flex-shrink-0`}
              title="Dispensar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
